import type { TFunction } from 'i18next';
import { DefinitionList, type DefinitionItem } from '../../components/ui/DefinitionList';
import { formatDecimal, formatMeasurement } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { type FieldVisibility, type RunFieldDescriptor, type RunFieldGroup, visibleGroups } from './run-fields';

/** Measurements are recorded to at most three decimals, which is what the numeric columns store. */
const MAX_FRACTION_DIGITS = 3;

const displayValue = <TValues,>(field: RunFieldDescriptor<TValues>, value: unknown, t: TFunction): string => {
  if (value === null || value === undefined || value === '') {
    return t('common.notSet');
  }

  if (field.kind === 'boolean') {
    return value ? t('common.yes') : t('common.no');
  }

  // A select stores its domain value (`MEDIUM`); the list reads the same translated word the form offered.
  if (field.kind === 'select') {
    return t(`${field.optionLabelPrefix ?? ''}.${String(value)}`);
  }

  if (field.kind === 'number' && typeof value === 'number') {
    return field.unit ? formatMeasurement(value, field.unit, MAX_FRACTION_DIGITS) : formatDecimal(value, MAX_FRACTION_DIGITS);
  }

  return String(value);
};

export interface RunFieldListProps<TValues> {
  groups: readonly RunFieldGroup<TValues>[];
  /** The saved conditions or measurements; `null` while that half of the run is still blank. */
  values: Record<string, unknown> | null;
  visibility: FieldVisibility;
  emptyMessage: string;
}

/**
 * Renders the same field descriptors the wizard collects, so a run's detail page and its review step can never
 * label a number differently from the form that captured it.
 */
export const RunFieldList = <TValues,>({ groups, values, visibility, emptyMessage }: RunFieldListProps<TValues>) => {
  const { t } = useI18nTranslation();

  if (values === null) {
    return (
      <div className="rounded-lg border border-border bg-card p-8">
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {visibleGroups(groups, visibility).map((group) => {
        const items: DefinitionItem[] = group.fields.map((field) => ({
          label: t(field.labelKey),
          value: displayValue(field, values[field.name], t),
        }));

        return (
          <section
            key={group.titleKey}
            className="rounded-lg border border-border bg-card p-5"
          >
            <h3 className="text-sm font-medium text-card-foreground">{t(group.titleKey)}</h3>
            <DefinitionList
              className="mt-2"
              items={items}
            />
          </section>
        );
      })}
    </div>
  );
};
